/**
 * VÉRAULT Travels — Public Trip Landing Page (Wave 26.18j)
 * ==========================================================
 *
 * Mounted at `/trip/:slug` (no auth). Renders a luxurious, calm,
 * editorial composition of an itinerary suitable for sharing with
 * prospective travelers — especially useful for group trips.
 *
 * Visual language: full-bleed hero with serif headline, paper
 * background, 12-col editorial grid, generous spacing, day-by-day
 * teasers with hero images, CTA at the foot.
 */
import React from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  MapPin,
  Loader2,
  Plane,
  Hotel,
  Car,
  Sun,
  Coffee,
  Moon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDateRange(s, e) {
  if (!s) return null;
  try {
    const start = new Date(s);
    const end = e ? new Date(e) : null;
    const opts = { month: "long", day: "numeric", year: "numeric" };
    if (!end) return start.toLocaleDateString([], opts);
    if (start.getFullYear() === end.getFullYear()) {
      const sShort = start.toLocaleDateString([], { month: "long", day: "numeric" });
      const eFull = end.toLocaleDateString([], opts);
      return `${sShort} — ${eFull}`;
    }
    return `${start.toLocaleDateString([], opts)} — ${end.toLocaleDateString([], opts)}`;
  } catch { return null; }
}

const BLOCK_META = {
  morning: { label: "Morning", icon: Coffee },
  afternoon: { label: "Afternoon", icon: Sun },
  evening: { label: "Evening", icon: Moon },
};

function SlotLine({ item }) {
  if (!item) return null;
  return (
    <li className="flex items-start gap-2 text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]" data-testid={`landing-item-${item.id}`}>
      <span className="mt-1.5 inline-block size-1 rounded-full bg-[hsl(var(--accent-gold))] flex-shrink-0" />
      <span className="min-w-0">
        {item.time && <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] mr-2">{item.time}</span>}
        <span className="font-serif text-[hsl(var(--foreground))]">{item.name}</span>
        {item.address && <span className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]"> · {item.address}</span>}
      </span>
    </li>
  );
}

function DayCard({ day, index }) {
  // Pick a hero — first slot image we find
  const heroImage =
    day.morning?.meal_slot?.image_url ||
    day.morning?.primary_activity?.image_url ||
    day.afternoon?.primary_activity?.image_url ||
    day.evening?.meal_slot?.image_url ||
    day.afternoon?.meal_slot?.image_url ||
    day.evening?.primary_activity?.image_url ||
    null;

  return (
    <article className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start" data-testid={`landing-day-${day.day_number}`}>
      <div className="lg:col-span-5">
        {heroImage ? (
          <div className="aspect-[4/3] lg:aspect-[3/4] overflow-hidden rounded-lg bg-[hsl(var(--background-paper))]">
            <img src={heroImage} alt={day.day_title || `Day ${day.day_number}`} loading="lazy" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[4/3] lg:aspect-[3/4] rounded-lg bg-[hsl(var(--background-paper))]" />
        )}
      </div>
      <div className="lg:col-span-7 lg:pt-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
          Day {day.day_number}{day.day_of_week ? ` · ${day.day_of_week}` : ""}
        </div>
        <h3 className="mt-2 font-serif text-[28px] sm:text-[34px] leading-tight text-[hsl(var(--foreground))]">
          {day.day_title || `Chapter ${index + 1}`}
        </h3>
        {day.day_notes && (
          <p className="mt-3 font-accent italic text-[15.5px] leading-relaxed text-[hsl(var(--foreground-secondary))] max-w-prose">
            {day.day_notes}
          </p>
        )}
        <div className="mt-5 space-y-4">
          {["morning", "afternoon", "evening"].map((bk) => {
            const block = day[bk];
            if (!block) return null;
            const items = [block.meal_slot, block.primary_activity, block.secondary_activity, ...(block.extra_items || [])].filter(Boolean);
            if (items.length === 0) return null;
            const M = BLOCK_META[bk];
            const Icon = M.icon;
            return (
              <div key={bk}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">{M.label}</span>
                </div>
                <ul className="space-y-1.5 pl-1">
                  {items.map((it) => <SlotLine key={it.id} item={it} />)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}

function LogisticsSummary({ snapshot }) {
  const { stays = [], flights = [], transfers = [] } = snapshot || {};
  if (stays.length === 0 && flights.length === 0 && transfers.length === 0) return null;
  return (
    <section className="border-t border-[rgba(0,0,0,0.08)] pt-12" data-testid="landing-logistics">
      <h2 className="font-serif text-[28px] sm:text-[34px] text-[hsl(var(--foreground))] mb-8">Where you'll be, how you'll move</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
        {stays.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Hotel className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <h3 className="font-serif text-[17px] text-[hsl(var(--foreground))]">Where you'll stay</h3>
            </div>
            <ul className="space-y-3">
              {stays.map((s) => (
                <li key={s.id} className="flex gap-3" data-testid={`landing-stay-${s.id}`}>
                  {s.image_url && <img src={s.image_url} alt="" loading="lazy" className="w-16 h-16 rounded object-cover flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-serif text-[15px] text-[hsl(var(--foreground))] truncate">{s.vendor}</div>
                    {s.address && <div className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] truncate">{s.address}</div>}
                    {s.room_category && <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] mt-0.5">{s.room_category}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {flights.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Plane className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <h3 className="font-serif text-[17px] text-[hsl(var(--foreground))]">How you'll fly</h3>
            </div>
            <ul className="space-y-2.5">
              {flights.map((f) => (
                <li key={f.id} className="font-serif text-[14.5px] text-[hsl(var(--foreground))]" data-testid={`landing-flight-${f.id}`}>
                  {f.airline || f.vendor} {f.flight_number ? `· ${f.flight_number}` : ""}
                  {(f.origin_iata || f.destination_iata) && (
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] tabular-nums">
                      {f.origin_iata || "—"} → {f.destination_iata || "—"}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {transfers.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Car className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <h3 className="font-serif text-[17px] text-[hsl(var(--foreground))]">How you'll get there</h3>
            </div>
            <ul className="space-y-2.5">
              {transfers.map((t) => (
                <li key={t.id} className="font-serif text-[14.5px] text-[hsl(var(--foreground))]" data-testid={`landing-transfer-${t.id}`}>
                  {t.supplier || t.vendor || t.vehicle_type}
                  {(t.pickup_location || t.dropoff_location) && (
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] truncate">
                      {t.pickup_location || "—"} → {t.dropoff_location || "—"}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/landing/${slug}`)
      .then((res) => { if (!cancelled) { setPage(res.data); setLoading(false); } })
      .catch((e) => {
        if (!cancelled) {
          setErr(e?.response?.status === 404 ? "This trip page isn't available." : "Couldn't load the page.");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background-canvas))]">
        <Loader2 className="size-6 animate-spin text-[hsl(var(--accent-gold))]" />
      </div>
    );
  }
  if (err || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[hsl(var(--background-canvas))] px-6">
        <p className="font-serif text-[22px] text-[hsl(var(--foreground))]">{err || "Not found."}</p>
        <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">Please contact your advisor for the latest link.</p>
      </div>
    );
  }

  const snapshot = page.snapshot || {};
  const dateLine = fmtDateRange(snapshot.trip_start_date, snapshot.trip_end_date);

  return (
    <div className="min-h-screen bg-[hsl(var(--background-canvas))] text-[hsl(var(--foreground))]" data-testid="landing-page-root">
      {/* HERO */}
      <header className="relative isolate" data-testid="landing-hero">
        <div className="relative aspect-[16/9] sm:aspect-[21/9] lg:aspect-[2.5/1] w-full bg-[hsl(var(--background-paper))] overflow-hidden">
          {page.hero_image && (
            <img src={page.hero_image} alt={page.title} loading="eager" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-5xl mx-auto px-5 sm:px-8 pb-8 sm:pb-14">
              <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-white/80 mb-3">VÉRAULT · An Experience</p>
              <h1 className="font-serif text-white text-[36px] sm:text-[52px] lg:text-[64px] leading-[1.05] tracking-tight">
                {page.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-white/85 font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.22em]">
                {dateLine && (
                  <span className="inline-flex items-center gap-1.5"><Calendar className="size-3.5" strokeWidth={1.5} />{dateLine}</span>
                )}
                {snapshot.destinations && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5" strokeWidth={1.5} />{snapshot.destinations}</span>
                )}
                {snapshot.duration_days > 0 && (
                  <span>{snapshot.duration_days} {snapshot.duration_days === 1 ? "day" : "days"}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
        {page.intro && (
          <p className="font-accent italic text-[18px] sm:text-[21px] leading-relaxed text-[hsl(var(--foreground-secondary))]" data-testid="landing-intro">
            {page.intro}
          </p>
        )}
      </section>

      {/* DAYS */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-20 space-y-16 sm:space-y-24" data-testid="landing-days">
        {(snapshot.days || []).length === 0 ? (
          <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-muted))] text-center">
            Day-by-day chapters arriving shortly.
          </p>
        ) : (
          snapshot.days.map((d, i) => <DayCard key={d.day_number} day={d} index={i} />)
        )}
      </section>

      {/* LOGISTICS SUMMARY */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
        <LogisticsSummary snapshot={snapshot} />
      </section>

      {/* CTA */}
      {(page.cta_label || page.cta_url) && (
        <section className="border-t border-[rgba(0,0,0,0.08)] py-16 sm:py-24 bg-[hsl(var(--background-paper))]/40">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <h2 className="font-serif text-[28px] sm:text-[34px] text-[hsl(var(--foreground))]">Travel with us</h2>
            <p className="mt-3 font-accent italic text-[16px] text-[hsl(var(--foreground-secondary))]">
              Spaces are limited. Reach out for itinerary tailoring and availability.
            </p>
            {page.cta_url ? (
              <a
                href={page.cta_url}
                className="mt-6 inline-flex items-center justify-center px-6 py-3 rounded-full bg-[hsl(var(--accent-gold))] text-white font-mono text-[11px] uppercase tracking-[0.24em] hover:bg-[hsl(var(--accent-gold))]/90 transition-colors"
                data-testid="landing-cta"
              >
                {page.cta_label}
              </a>
            ) : (
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--foreground-muted))]">
                Contact your advisor — {page.cta_label}
              </p>
            )}
          </div>
        </section>
      )}

      <footer className="py-10 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--foreground-muted))]">
        VÉRAULT TRAVELS · Curated Experiences
      </footer>
    </div>
  );
}
